import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Ruler, Save, LogOut, User, Settings, Users, FolderOpen, Folder, Printer, DatabaseBackup } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getSupportedGeometryVersions } from '@/lib/drafting';

export default function TopBar({ 
  currentPattern,
  onHomeClick,
  onLoadPattern,
  onManagePatterns,
  onSave,
  onExport,
  onTogglePrintPreview,
  isPrintPreviewMode = false,
  onProfileClick,
  onAdminClick,
  onBackupClick,
  canManagePatterns = false,
  canSavePatternVersion = true,
  patternEditLocked = false,
  onUnlockPattern,
  canAccessUserManagement = false,
  canAccessBackupManagement = false,
  onGeometryVersionChange,
}) {
  const { user, logout } = useAuth();
  
  const displayName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}`
    : user?.email;

  const currentGeometryVersion = currentPattern
    ? (currentPattern.geometry_version || currentPattern?.pattern_data?.geometryVersion || currentPattern.formula_version || 'legacy')
    : null;

  const currentGarmentType = (currentPattern?.garment_type || 'trouser').toLowerCase();
  const geometryOptions = currentPattern ? getSupportedGeometryVersions(currentGarmentType) : [];
  const orderedGeometryOptions = [...geometryOptions];

  const handleGeometrySelect = (nextGeometryVersion) => {
    if (!currentPattern || !nextGeometryVersion || nextGeometryVersion === currentGeometryVersion) return;

    const firstConfirm = window.confirm(
      `Change geometry from ${currentGeometryVersion} to ${nextGeometryVersion}?\n\nThis will regenerate shape using current measurements.`
    );
    if (!firstConfirm) return;

    const secondConfirmText = window.prompt(
      `Type SWITCH to confirm geometry change to ${nextGeometryVersion}`,
      ''
    );

    if ((secondConfirmText || '').trim().toUpperCase() !== 'SWITCH') {
      window.alert('Geometry switch cancelled (confirmation text mismatch).');
      return;
    }

    onGeometryVersionChange?.(nextGeometryVersion);
  };

  return (
    <div className="h-16 border-b bg-card flex items-center justify-between px-6 flex-shrink-0 shadow-sm">
      {/* Left Side - Logo & File Actions */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          className="flex items-center gap-3 rounded-md text-left transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          onClick={onHomeClick}
          aria-label="Go to PatternEngine home"
          data-testid="topbar-home-logo-button"
        >
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
            <Ruler size={20} />
          </div>
          <div>
            <h1 className="text-[13px] font-semibold tracking-tight">PatternEngine</h1>
            <p className="text-[10px] font-normal text-muted-foreground uppercase tracking-wider">Professional CAD</p>
          </div>
        </button>
        
        <div className="h-8 w-px bg-border" />
        
        {/* File Actions */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={onLoadPattern} data-testid="topbar-load-pattern-button">
            <FolderOpen size={14} /> Load
          </Button>
          {canManagePatterns && (
            <Button variant="outline" size="sm" className="gap-2" onClick={onManagePatterns} data-testid="topbar-manage-patterns-button">
              <Folder size={14} /> Manage Patterns
            </Button>
          )}
        </div>
        
        {currentPattern && (
          <>
            <div className="h-8 w-px bg-border" />
            
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Client:</span>
              <span className="text-[12px] font-medium">#{currentPattern.client_number} - {currentPattern.client_name}</span>
              <span className="text-xs text-muted-foreground">|</span>
              <span className="text-xs font-medium uppercase text-slate-600" data-testid="current-garment-type-label">{currentPattern.garment_type}</span>
              <span className="text-xs text-muted-foreground">|</span>
              <span className="text-[11px] font-mono font-medium tabular-nums text-primary">{currentPattern.pattern_number}</span>
              <span
                className="text-[11px] font-mono font-medium tabular-nums rounded-full px-2 py-1 border bg-sky-50 text-sky-700 border-sky-300"
                data-testid="topbar-geometry-version-badge"
              >
                GEO {currentGeometryVersion}
              </span>

              {canSavePatternVersion && geometryOptions.length > 1 && (
                <div className="min-w-[170px]" data-testid="topbar-geometry-version-control-wrap">
                  <Select
                    value={currentGeometryVersion}
                    onValueChange={handleGeometrySelect}
                  >
                    <SelectTrigger className="h-8" data-testid="topbar-geometry-version-select-trigger">
                      <SelectValue placeholder="Geometry" />
                    </SelectTrigger>
                    <SelectContent>
                      {orderedGeometryOptions.map((version) => (
                        <SelectItem
                          key={version}
                          value={version}
                          data-testid={`topbar-geometry-version-option-${version}`}
                        >
                          {version}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Right Side - Save/Export & User Menu */}
      <div className="flex items-center gap-3">
        {currentPattern && (
          <>
            <Button
              variant={isPrintPreviewMode ? 'default' : 'outline'}
              size="sm"
              className="gap-2"
              onClick={onTogglePrintPreview}
              data-testid="topbar-print-preview-toggle-button"
            >
              <Printer size={14} /> {isPrintPreviewMode ? 'Draft Mode' : 'Print Preview'}
            </Button>
            {patternEditLocked && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-amber-400 text-amber-800"
                onClick={onUnlockPattern}
                data-testid="topbar-unlock-pattern-button"
              >
                Unlock
              </Button>
            )}
            {canSavePatternVersion && (
              <Button variant="outline" size="sm" className="gap-2" onClick={onSave} data-testid="topbar-save-version-button">
                <Save size={14} /> Save Version
              </Button>
            )}
            <div className="h-6 w-px bg-border mx-1" />
          </>
        )}
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <User size={16} />
              <span className="text-sm">{displayName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuLabel className="font-normal text-xs text-muted-foreground">
              {user?.role?.toUpperCase()}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onProfileClick}>
              <Settings size={14} className="mr-2" />
              Profile Settings
            </DropdownMenuItem>
            {canAccessUserManagement && (
              <DropdownMenuItem onClick={onAdminClick}>
                <Users size={14} className="mr-2" />
                User Management
              </DropdownMenuItem>
            )}
            {canAccessBackupManagement && (
              <DropdownMenuItem onClick={onBackupClick} data-testid="topbar-backup-management-menu-item">
                <DatabaseBackup size={14} className="mr-2" />
                Backup Management
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut size={14} className="mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
